type AnalyticsEvent =
  | "app_opened"
  | "search_performed"
  | "municipality_selected"
  | "tariff_plan_selected"
  | "map_period_scrubbed"
  | "usage_changed"
  | "fbe_assumption_toggled"
  | "real_terms_toggled"
  | "compare_selection_added"
  | "compare_selection_removed"
  | "share_clicked"
  | "calculation_disclosure_opened"
  | "data_table_opened"
  | "methodology_opened";

type SafeProperty = string | number | boolean | undefined;
type AnalyticsProperties = Record<string, SafeProperty>;

let clientPromise: Promise<typeof import("posthog-js").default | null> | undefined;

function privacySignalEnabled(): boolean {
  const globalPrivacyControl = (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl;
  return navigator.doNotTrack === "1" || globalPrivacyControl === true;
}

function getClient() {
  if (clientPromise) return clientPromise;

  const key = import.meta.env.VITE_PUBLIC_POSTHOG_KEY as string | undefined;
  const host = (import.meta.env.VITE_PUBLIC_POSTHOG_HOST as string | undefined) || "https://eu.i.posthog.com";

  if (!key || key === "your_posthog_project_token") {
    clientPromise = Promise.resolve(null);
    return clientPromise;
  }

  if (privacySignalEnabled()) {
    clientPromise = Promise.resolve(null);
    return clientPromise;
  }

  clientPromise = import("posthog-js").then(({ default: posthog }) => {
    posthog.init(key, {
      api_host: host,
      defaults: "2026-05-30",
      persistence: "memory",
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      disable_session_recording: true,
      person_profiles: "never",
      opt_out_capturing_by_default: false,
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: false,
      },
    });
    return posthog;
  }).catch(() => null);

  return clientPromise;
}

export function initializePostHog() {
  void getClient();
}

export function track(event: AnalyticsEvent, properties: AnalyticsProperties = {}) {
  void getClient().then((client) => client?.capture(event, properties));
}

export function usageBucket(usage: number): string {
  if (usage < 200) return "0-199";
  if (usage < 400) return "200-399";
  if (usage < 700) return "400-699";
  if (usage < 1000) return "700-999";
  return "1000+";
}
